/*
 * cx-logo-customiser.js
 *
 * "Your Business Logo on Your Laptop / T-shirt" customiser (Oct 2026).
 * Self-contained controller for snippets/cx-logo-customiser.liquid. Does NOT
 * use (and must never be loaded alongside) custom-personalization-multi-add.js,
 * -image.js or -crop.js: those bind to every [data-custom-personalization]
 * root, and this root carries data-mode="image" only so the shared AR
 * preview (ar-patch-preview.js, image branch) can pick up the composed
 * patch from [data-custom-image-preview] img with zero changes to that file.
 *
 * Flow:
 *   upload logo (PNG/JPG/WEBP/SVG) -> optional crop -> optional client-side
 *   background removal (border-colour keying, flood fill from the edges or
 *   global, soft edge + colour de-matting) -> auto-trim -> optional one line
 *   of text under the logo -> live mockup on a laptop lid / T-shirt chest
 *   sized proportionally to the chosen size -> on Add to cart the design is
 *   flattened into ONE transparent PNG at 300 dpi of the product's largest
 *   size and uploaded as the "Custom Image" line-item property, exactly like
 *   the live Customise Your Own Image products. Operations OS needs no new
 *   parsing: it prints that one image at the size from the variant title
 *   (or "Custom Size (inches)").
 *
 * Other line-item properties are informational only and are written so
 * they can never start with "http" (customized-demand.ts takes the FIRST
 * http-valued property as the print file): the text line is wrapped in
 * quotes, everything else is a fixed word.
 */
(function () {
  'use strict';

  var FONTS = {
    montserrat: { family: 'cx_logo_montserrat', weight: 700, label: 'Montserrat' },
    poppins: { family: 'cx_logo_poppins', weight: 600, label: 'Poppins' },
    playfair: { family: 'cx_logo_playfair', weight: 700, label: 'Playfair Display' },
    oswald: { family: 'cx_logo_oswald', weight: 600, label: 'Oswald' },
    dancing: { family: 'cx_logo_dancing', weight: 700, label: 'Dancing Script' },
  };

  // Mockup geometry, in the inline SVG's own viewBox units. Calibrated so a
  // real-world inch maps to a fixed number of units: the laptop lid is 12in
  // wide (same assumption Akshat signed off on for Your Name on Laptop, 6in =
  // half the lid), the T-shirt torso is 20in wide (adult M chest, flat).
  var SURFACES = {
    laptop: {
      viewW: 1000,
      viewH: 700,
      unitsPerInch: 880 / 12,
      place: function () {
        return { x: 500, y: 345, anchor: 'center' };
      },
    },
    tshirt: {
      viewW: 1000,
      viewH: 1000,
      unitsPerInch: 540 / 20,
      place: function (sizeIn) {
        // Classic left-chest logo for small sizes (wearer's left = viewer's
        // right), centre chest once it is too big to sit on the chest pocket
        // area. Purely a preview choice; the patch can go anywhere.
        if (sizeIn <= 4.5) return { x: 640, y: 262, anchor: 'top' };
        return { x: 500, y: 240, anchor: 'top' };
      },
    },
  };

  var PRINT_DPI = 300;
  var LOW_DPI_WARN = 150;
  var MAX_FILE_BYTES = 20 * 1024 * 1024;
  var MAX_DIMENSION_PX = 8000;
  var SOURCE_CAP_PX = 4000; // memory guard; still 400 dpi at the 10in max
  var WORKING_CAP_PX = 900; // live preview processing resolution
  var SVG_RASTER_PX = 3200;
  var TEXT_MAX = 30;

  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  function scaleCanvas(src, maxLong) {
    var long = Math.max(src.width, src.height);
    if (long <= maxLong) return { canvas: src, scale: 1 };
    var k = maxLong / long;
    var out = makeCanvas(src.width * k, src.height * k);
    var ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, out.width, out.height);
    return { canvas: out, scale: out.width / src.width };
  }

  /* ------------------------------------------------------------------ */
  /* Background detection + removal                                      */
  /* ------------------------------------------------------------------ */

  // Looks only at the outermost 2px ring. Returns whether the image already
  // has a transparent background, and otherwise the dominant border colour
  // (5-bit-per-channel histogram, then the true mean of the winning bucket).
  function analyseBorder(data, w, h) {
    var buckets = {};
    var transparent = 0;
    var total = 0;
    function visit(x, y) {
      var i = (y * w + x) * 4;
      total++;
      if (data[i + 3] < 24) {
        transparent++;
        return;
      }
      var key = ((data[i] >> 3) << 10) | ((data[i + 1] >> 3) << 5) | (data[i + 2] >> 3);
      var b = buckets[key];
      if (!b) b = buckets[key] = { n: 0, r: 0, g: 0, b: 0 };
      b.n++;
      b.r += data[i];
      b.g += data[i + 1];
      b.b += data[i + 2];
    }
    var ring = Math.min(2, Math.floor(Math.min(w, h) / 2));
    for (var r = 0; r < ring; r++) {
      for (var x = 0; x < w; x++) {
        visit(x, r);
        visit(x, h - 1 - r);
      }
      for (var y = 0; y < h; y++) {
        visit(r, y);
        visit(w - 1 - r, y);
      }
    }
    var best = null;
    Object.keys(buckets).forEach(function (k) {
      if (!best || buckets[k].n > best.n) best = buckets[k];
    });
    var opaque = total - transparent;
    return {
      alreadyTransparent: total > 0 && transparent / total > 0.5,
      bg: best ? [best.r / best.n, best.g / best.n, best.b / best.n] : [255, 255, 255],
      // How uniform the border is: a photo-like background (gradient, scene)
      // will have a low share and removal will be patchy -- surfaced as a hint.
      uniformity: best && opaque ? best.n / opaque : 0,
    };
  }

  // strength 0..100 -> colour distance threshold. Soft band above it gives
  // anti-aliased edges instead of a jagged cut-out.
  function thresholds(strength) {
    var t = 6 + (clamp(strength, 0, 100) / 100) * 120;
    return { hard: t, soft: t * 1.35 + 8 };
  }

  function removeBackground(canvas, bg, strength, everywhere) {
    var w = canvas.width;
    var h = canvas.height;
    var ctx = canvas.getContext('2d');
    var img = ctx.getImageData(0, 0, w, h);
    var d = img.data;
    var n = w * h;
    var th = thresholds(strength);
    var br = bg[0];
    var bgG = bg[1];
    var bb = bg[2];

    var dist = new Float32Array(n);
    for (var p = 0, i = 0; p < n; p++, i += 4) {
      var dr = d[i] - br;
      var dg = d[i + 1] - bgG;
      var db = d[i + 2] - bb;
      // Mild perceptual weighting (green counts most).
      dist[p] = Math.sqrt(0.3 * dr * dr + 0.45 * dg * dg + 0.25 * db * db) * 1.5;
    }

    var mask = new Uint8Array(n); // 1 = part of the background region
    if (everywhere) {
      for (var q = 0; q < n; q++) if (dist[q] < th.soft) mask[q] = 1;
    } else {
      // Flood fill from every border pixel through "close to background"
      // pixels, so same-coloured areas INSIDE the logo (white letters on a
      // white-background logo, etc.) are kept.
      var stack = new Int32Array(n);
      var sp = 0;
      function seed(px) {
        if (!mask[px] && dist[px] < th.soft) {
          mask[px] = 1;
          stack[sp++] = px;
        }
      }
      for (var x = 0; x < w; x++) {
        seed(x);
        seed((h - 1) * w + x);
      }
      for (var y = 0; y < h; y++) {
        seed(y * w);
        seed(y * w + w - 1);
      }
      while (sp > 0) {
        var cur = stack[--sp];
        var cx = cur % w;
        if (cx > 0) seed(cur - 1);
        if (cx < w - 1) seed(cur + 1);
        if (cur >= w) seed(cur - w);
        if (cur < n - w) seed(cur + w);
      }

      // Small enclosed pockets of the background colour are letter counters
      // (inside of O, A, D, R...) and should go too; big enclosed areas of
      // that colour (a white cup, a cream badge) are part of the logo and
      // stay. Threshold: 0.5% of the image area per pocket.
      var maxPocket = Math.max(16, Math.round(n * 0.005));
      var seen = new Uint8Array(n);
      var comp = new Int32Array(n);
      for (var start = 0; start < n; start++) {
        if (mask[start] || seen[start] || dist[start] >= th.soft) continue;
        var cn = 0;
        var tooBig = false;
        sp = 0;
        stack[sp++] = start;
        seen[start] = 1;
        while (sp > 0) {
          var c2 = stack[--sp];
          if (cn < maxPocket) comp[cn] = c2;
          cn++;
          if (cn > maxPocket) tooBig = true;
          var x2 = c2 % w;
          var nb = [x2 > 0 ? c2 - 1 : -1, x2 < w - 1 ? c2 + 1 : -1, c2 >= w ? c2 - w : -1, c2 < n - w ? c2 + w : -1];
          for (var z = 0; z < 4; z++) {
            var nn = nb[z];
            if (nn >= 0 && !seen[nn] && !mask[nn] && dist[nn] < th.soft) {
              seen[nn] = 1;
              stack[sp++] = nn;
            }
          }
        }
        if (!tooBig) for (var m2 = 0; m2 < cn; m2++) mask[comp[m2]] = 1;
      }
    }

    var band = th.soft - th.hard;
    for (var k = 0, j = 0; k < n; k++, j += 4) {
      if (!mask[k]) continue;
      var dd = dist[k];
      if (dd <= th.hard) {
        d[j + 3] = 0;
        continue;
      }
      var a = (dd - th.hard) / band; // 0..1 across the soft edge
      a = a * a * (3 - 2 * a);
      var origA = d[j + 3] / 255;
      var na = a * origA;
      if (na <= 0.004) {
        d[j + 3] = 0;
        continue;
      }
      // De-matte: remove the background colour that was blended into the
      // edge pixel, so a white halo doesn't print around the logo.
      d[j] = clamp((d[j] - (1 - a) * br) / a, 0, 255);
      d[j + 1] = clamp((d[j + 1] - (1 - a) * bgG) / a, 0, 255);
      d[j + 2] = clamp((d[j + 2] - (1 - a) * bb) / a, 0, 255);
      d[j + 3] = Math.round(na * 255);
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  }

  // Crops away fully transparent margins so the chosen size applies to the
  // visible logo, not to empty padding around it.
  function trimTransparent(canvas) {
    var w = canvas.width;
    var h = canvas.height;
    var d = canvas.getContext('2d').getImageData(0, 0, w, h).data;
    var minX = w;
    var minY = h;
    var maxX = -1;
    var maxY = -1;
    for (var y = 0; y < h; y++) {
      var row = y * w * 4;
      for (var x = 0; x < w; x++) {
        if (d[row + x * 4 + 3] > 10) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    var out = makeCanvas(maxX - minX + 1, maxY - minY + 1);
    out.getContext('2d').drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  // Most common clearly-coloured (not near-white, not transparent) colour of
  // the finished logo -- offered as the "Logo colour" swatch for the text.
  function dominantInkColour(canvas) {
    var s = scaleCanvas(canvas, 160).canvas;
    var d = s.getContext('2d').getImageData(0, 0, s.width, s.height).data;
    var buckets = {};
    var best = null;
    for (var i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) continue;
      var lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      if (lum > 235) continue;
      var key = ((d[i] >> 4) << 8) | ((d[i + 1] >> 4) << 4) | (d[i + 2] >> 4);
      var b = buckets[key] || (buckets[key] = { n: 0, r: 0, g: 0, b: 0 });
      b.n++;
      b.r += d[i];
      b.g += d[i + 1];
      b.b += d[i + 2];
      if (!best || b.n > best.n) best = b;
    }
    if (!best) return null;
    function hex(v) {
      var s2 = Math.round(v / best.n).toString(16);
      return s2.length === 1 ? '0' + s2 : s2;
    }
    return '#' + hex(best.r) + hex(best.g) + hex(best.b);
  }

  /* ------------------------------------------------------------------ */
  /* Text line + composition                                             */
  /* ------------------------------------------------------------------ */

  function fontString(font, px) {
    return font.weight + ' ' + px + 'px "' + font.family + '", sans-serif';
  }

  function greedyTwoLines(ctx, words) {
    // Best balanced split into two lines (minimise the wider line).
    var best = null;
    for (var i = 1; i < words.length; i++) {
      var a = words.slice(0, i).join(' ');
      var b = words.slice(i).join(' ');
      var wMax = Math.max(ctx.measureText(a).width, ctx.measureText(b).width);
      if (!best || wMax < best.w) best = { lines: [a, b], w: wMax };
    }
    return best;
  }

  // Lays the text out for a logo of logoW x logoH (any units). Font size is
  // tied to the logo's visual mass, never squished horizontally: one line if
  // it fits, else a balanced 2-line wrap, else uniformly smaller.
  function layoutText(text, font, logoW, logoH) {
    var ctx = makeCanvas(4, 4).getContext('2d');
    var basePx = 0.17 * Math.sqrt(logoW * logoH);
    basePx = Math.min(basePx, logoH * 0.42);
    var maxW = Math.max(logoW * 1.15, logoH * 1.3);
    var PROBE = 100;
    ctx.font = fontString(font, PROBE);
    var k = basePx / PROBE;
    var lines = [text];
    var widthAtProbe = ctx.measureText(text).width;
    if (widthAtProbe * k > maxW) {
      var words = text.split(/\s+/).filter(Boolean);
      var two = words.length > 1 ? greedyTwoLines(ctx, words) : null;
      if (two && two.w * k <= maxW) {
        lines = two.lines;
        widthAtProbe = two.w;
      } else if (two && two.w < widthAtProbe * 0.8) {
        lines = two.lines;
        widthAtProbe = two.w;
        k = maxW / widthAtProbe;
      } else {
        k = maxW / widthAtProbe;
      }
    }
    var px = PROBE * k;
    var lineH = px * 1.18;
    return {
      lines: lines,
      px: px,
      lineH: lineH,
      width: widthAtProbe * k,
      height: lineH * lines.length,
      gap: Math.max(px * 0.35, logoH * 0.06),
    };
  }

  // Flattens logo (+ optional text) into one transparent canvas whose LONG
  // edge is longEdgePx. This exact function produces both the on-page
  // preview and the print file, so what the customer sees is what prints.
  function compose(logo, textOpts, longEdgePx) {
    var lw = logo.width;
    var lh = logo.height;
    var lay = null;
    if (textOpts && textOpts.text) lay = layoutText(textOpts.text, textOpts.font, lw, lh);
    var compW = Math.max(lw, lay ? lay.width : 0);
    var compH = lh + (lay ? lay.gap + lay.height : 0);
    // Tiny safety margin so anti-aliased glyph edges are never clipped.
    var pad = Math.max(compW, compH) * 0.01;
    compW += pad * 2;
    compH += pad * 2;
    var s = longEdgePx / Math.max(compW, compH);
    var out = makeCanvas(compW * s, compH * s);
    var ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(logo, ((compW - lw) / 2) * s, pad * s, lw * s, lh * s);
    if (lay) {
      ctx.font = fontString(textOpts.font, lay.px * s);
      ctx.fillStyle = textOpts.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      var top = pad + lh + lay.gap;
      lay.lines.forEach(function (line, idx) {
        // Baseline at ~82% of the line box keeps descenders inside.
        var y = top + lay.lineH * idx + lay.lineH * 0.8;
        ctx.fillText(line, (compW / 2) * s, y * s);
      });
    }
    return {
      canvas: out,
      // Fraction of the composition's width/height the logo itself spans,
      // used for the effective-dpi check.
      logoWidthFraction: lw / compW,
      logoHeightFraction: lh / compH,
    };
  }

  // Composes slightly oversize, trims every transparent margin (the safety
  // pad, glyph side-bearings), then scales so the VISIBLE design's long edge
  // is exactly longEdgePx. The chosen size (e.g. 4in) therefore maps to the
  // real ink, end to end, matching how Operations OS sizes the print.
  function composeExact(logo, text, longEdgePx) {
    var result = compose(logo, text, Math.round(longEdgePx * 1.04));
    var t = trimTransparent(result.canvas) || result.canvas;
    var fw = result.canvas.width / t.width;
    var fh = result.canvas.height / t.height;
    var k = longEdgePx / Math.max(t.width, t.height);
    var out = makeCanvas(t.width * k, t.height * k);
    var ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(t, 0, 0, out.width, out.height);
    return {
      canvas: out,
      logoWidthFraction: Math.min(1, result.logoWidthFraction * fw),
      logoHeightFraction: Math.min(1, result.logoHeightFraction * fh),
    };
  }

  /* ------------------------------------------------------------------ */
  /* File loading                                                        */
  /* ------------------------------------------------------------------ */

  function loadImage(src, crossOrigin) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('load'));
      };
      img.src = src;
    });
  }

  function readText(file) {
    if (file.text) return file.text();
    return new Promise(function (resolve, reject) {
      var r = new FileReader();
      r.onload = function () {
        resolve(r.result);
      };
      r.onerror = reject;
      r.readAsText(file);
    });
  }

  // SVG logos are rasterised big (3200px long edge) so they stay crisp at
  // every size. Explicit width/height are injected because many exported
  // SVGs only carry a viewBox, which browsers then draw at 300x150.
  function rasteriseSvg(file) {
    return readText(file).then(function (svgText) {
      var doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
      var svg = doc.documentElement;
      if (!svg || svg.nodeName.toLowerCase() !== 'svg') throw new Error('svg');
      var vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(parseFloat);
      var w = parseFloat(svg.getAttribute('width')) || (vb.length === 4 ? vb[2] : 0);
      var h = parseFloat(svg.getAttribute('height')) || (vb.length === 4 ? vb[3] : 0);
      if (!w || !h) {
        w = 1000;
        h = 1000;
      }
      if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      var k = SVG_RASTER_PX / Math.max(w, h);
      svg.setAttribute('width', String(Math.round(w * k)));
      svg.setAttribute('height', String(Math.round(h * k)));
      var blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' });
      var url = URL.createObjectURL(blob);
      return loadImage(url).then(function (img) {
        var c = makeCanvas(Math.round(w * k), Math.round(h * k));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        // Throws (SecurityError) if this browser taints SVG canvases -- caught
        // by the caller and turned into a "please upload PNG" message.
        c.getContext('2d').getImageData(0, 0, 1, 1);
        return { canvas: c, isVector: true };
      });
    });
  }

  function rasteriseBitmap(file) {
    var url = URL.createObjectURL(file);
    return loadImage(url).then(function (img) {
      URL.revokeObjectURL(url);
      if (img.naturalWidth > MAX_DIMENSION_PX || img.naturalHeight > MAX_DIMENSION_PX) {
        var e = new Error('dims');
        e.code = 'dims';
        throw e;
      }
      var c = makeCanvas(img.naturalWidth, img.naturalHeight);
      c.getContext('2d').drawImage(img, 0, 0);
      return { canvas: c, isVector: false };
    });
  }

  /* ------------------------------------------------------------------ */
  /* Controller                                                          */
  /* ------------------------------------------------------------------ */

  function init(root) {
    var surfaceKey = root.getAttribute('data-surface') === 'tshirt' ? 'tshirt' : 'laptop';
    var surface = SURFACES[surfaceKey];
    var maxSizeIn = parseFloat(root.getAttribute('data-max-size-in')) || 6;

    function q(sel) {
      return root.querySelector(sel);
    }

    var els = {
      stage: q('[data-cx-stage]'),
      placement: q('[data-cx-placement]'),
      mockCanvas: q('[data-cx-mockup-canvas]'),
      stageUpload: q('[data-cx-stage-upload]'),
      swatches: root.querySelectorAll('[data-cx-surface-swatch]'),
      file: q('[data-cx-file]'),
      fileError: q('[data-cx-file-error]'),
      fileErrorText: q('[data-cx-file-error-text]'),
      tools: q('[data-cx-logo-tools]'),
      cropChip: q('[data-cx-open-crop]'),
      replaceChip: q('[data-cx-replace]'),
      patchWrap: q('[data-cx-patch-wrap]'),
      patchCanvas: q('[data-cx-patch-canvas]'),
      patchDims: q('[data-cx-patch-dims]'),
      bgBlock: q('[data-cx-bg-block]'),
      bgToggle: q('[data-cx-bg-toggle]'),
      bgControls: q('[data-cx-bg-controls]'),
      bgStrength: q('[data-cx-bg-strength]'),
      bgEverywhere: q('[data-cx-bg-everywhere]'),
      bgAlready: q('[data-cx-bg-already]'),
      bgHint: q('[data-cx-bg-hint]'),
      lowRes: q('[data-cx-lowres]'),
      lowResText: q('[data-cx-lowres-text]'),
      step2: q('[data-cx-step="2"]'),
      textInput: q('[data-cx-text-input]'),
      textCount: q('[data-cx-text-count]'),
      textHint: q('[data-cx-text-hint]'),
      textOptions: q('[data-cx-text-options]'),
      fontSelect: q('[data-cx-font-select]'),
      colourRadios: root.querySelectorAll('[data-cx-colour-radio]'),
      colourLogoSwatch: q('[data-cx-colour-logo]'),
      colourCustom: q('[data-cx-colour-custom]'),
      colourCustomRadio: q('[data-cx-colour-custom-radio]'),
      verify: q('[data-cx-verify]'),
      arImg: q('[data-cx-ar-img]'),
      // size + cart (markup from cx-logo-sizes.liquid)
      submit: q('[data-custom-personalization-submit]'),
      submitText: q('[data-custom-personalization-submit-text]'),
      cartError: q('[data-custom-personalization-error]'),
      cartErrorText: q('[data-custom-personalization-error-text]'),
      primaryQty: q('[data-primary-qty-input]'),
      openMoreSizes: q('[data-open-more-sizes]'),
      price: q('[data-custom-personalization-price]'),
      customSize: q('[data-custom-size-input]'),
      customSizeField: q('[data-custom-size-field]'),
      customSizeError: q('[data-custom-size-error]'),
      customSizeErrorText: q('[data-custom-size-error-text]'),
      customSizeConfirm: q('[data-custom-size-confirm]'),
    };
    if (!els.stage || !els.file || !els.submit || !els.primaryQty) return;

    var cropModalSel = root.getAttribute('data-crop-modal');
    var cropModal = cropModalSel ? document.querySelector(cropModalSel) : null;

    var state = {
      original: null, // full-res source canvas (pre-crop), capped at SOURCE_CAP_PX
      originalScale: 1, // original canvas px per uploaded-file px (<=1)
      isVector: false,
      crop: null, // {x,y,w,h} in original canvas px, or null = full image
      working: null, // cropped + downscaled copy for live preview
      workingScale: 1, // working px per original-canvas px
      border: null,
      logoPreview: null, // processed + trimmed working logo
      logoColour: null,
      prefillUrl: null, // edit-from-cart: finished design URL, untouched so far
      prefillDirty: false,
      busy: false,
      customSizeValue: null,
      customSizeHasError: false,
    };

    /* ---------- helpers ---------- */

    function showFileError(msg) {
      els.fileErrorText.textContent = msg;
      els.fileError.hidden = false;
    }
    function clearFileError() {
      els.fileError.hidden = true;
      els.fileErrorText.textContent = '';
    }

    function selectedFont() {
      var opt = els.fontSelect.options[els.fontSelect.selectedIndex];
      return FONTS[(opt && opt.value) || 'montserrat'] || FONTS.montserrat;
    }
    function selectedFontLabel() {
      return selectedFont().label;
    }

    function selectedColour() {
      var checked = root.querySelector('[data-cx-colour-radio]:checked');
      if (!checked) return '#111111';
      if (checked === els.colourCustomRadio) return els.colourCustom.value || '#111111';
      return checked.value;
    }
    function selectedColourLabel() {
      var checked = root.querySelector('[data-cx-colour-radio]:checked');
      var hex = selectedColour().toUpperCase();
      var name = checked ? checked.getAttribute('data-colour-name') : '';
      return name ? name + ' ' + hex : hex;
    }

    function textValue() {
      return (els.textInput.value || '').replace(/\s+/g, ' ').trim().slice(0, TEXT_MAX);
    }

    function textOpts() {
      var t = textValue();
      if (!t) return null;
      return { text: t, font: selectedFont(), color: selectedColour() };
    }

    function sizeFromRadio(radio) {
      var label = radio && root.querySelector('label[for="' + radio.id + '"]');
      var m = label ? label.textContent.match(/(\d+(?:\.\d+)?)/) : null;
      return m ? parseFloat(m[1]) : null;
    }

    function selectedSizeIn() {
      if (state.customSizeValue) return parseFloat(state.customSizeValue);
      var checked = root.querySelector('[data-primary-size-radio]:checked');
      return sizeFromRadio(checked) || parseFloat(root.getAttribute('data-default-size')) || 3;
    }

    function hasLogo() {
      return !!(state.logoPreview || state.prefillUrl);
    }

    /* ---------- processing ---------- */

    function rebuildWorking() {
      var src = state.original;
      var c = state.crop;
      var cropped = src;
      if (c) {
        cropped = makeCanvas(c.w, c.h);
        cropped.getContext('2d').drawImage(src, c.x, c.y, c.w, c.h, 0, 0, c.w, c.h);
      }
      var scaled = scaleCanvas(cropped, WORKING_CAP_PX);
      // scaleCanvas may return the same canvas; copy so processing never
      // mutates the original.
      var work = makeCanvas(scaled.canvas.width, scaled.canvas.height);
      work.getContext('2d').drawImage(scaled.canvas, 0, 0);
      state.working = work;
      state.workingScale = scaled.scale;
      var data = work.getContext('2d').getImageData(0, 0, work.width, work.height).data;
      state.border = analyseBorder(data, work.width, work.height);
    }

    function bgSettings() {
      var already = state.border && state.border.alreadyTransparent;
      return {
        remove: !already && els.bgToggle.checked,
        strength: parseFloat(els.bgStrength.value) || 0,
        everywhere: !!els.bgEverywhere.checked,
        already: already,
      };
    }

    // Applies crop->bg removal->trim to a cropped source canvas (copied).
    function processLogo(cropped) {
      var s = bgSettings();
      var c = makeCanvas(cropped.width, cropped.height);
      c.getContext('2d').drawImage(cropped, 0, 0);
      if (s.remove) removeBackground(c, state.border.bg, s.strength, s.everywhere);
      return trimTransparent(c);
    }

    function reprocessPreview() {
      if (!state.working) return;
      var logo = processLogo(state.working);
      if (!logo) {
        state.logoPreview = null;
        showFileError('That setting removed your whole logo. Lower the strength slider.');
        render();
        return;
      }
      clearFileError();
      state.logoPreview = logo;
      state.logoColour = dominantInkColour(logo);
      if (els.colourLogoSwatch) {
        if (state.logoColour) {
          els.colourLogoSwatch.value = state.logoColour;
          var dot = els.colourLogoSwatch.nextElementSibling && els.colourLogoSwatch.nextElementSibling.querySelector('[data-cx-colour-dot]');
          if (dot) dot.style.background = state.logoColour;
          els.colourLogoSwatch.closest('[data-cx-colour-item]').hidden = false;
        } else {
          els.colourLogoSwatch.closest('[data-cx-colour-item]').hidden = true;
          if (els.colourLogoSwatch.checked) root.querySelector('[data-cx-colour-radio][data-colour-name="Black"]').checked = true;
        }
      }
      render();
    }

    /* ---------- rendering ---------- */

    var lastComposite = null;

    function drawCheckerPreview(comp) {
      var cv = els.patchCanvas;
      var maxW = 520;
      var k = Math.min(1, maxW / comp.width, 360 / comp.height);
      cv.width = Math.round(comp.width * k);
      cv.height = Math.round(comp.height * k);
      var ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(comp, 0, 0, cv.width, cv.height);
    }

    function placeOnMockup(comp) {
      var sizeIn = selectedSizeIn();
      var long = Math.max(comp.width, comp.height);
      var wIn = (sizeIn * comp.width) / long;
      var hIn = (sizeIn * comp.height) / long;
      var wUnits = wIn * surface.unitsPerInch;
      var hUnits = hIn * surface.unitsPerInch;
      var p = surface.place(sizeIn);
      var left = p.x - wUnits / 2;
      var top = p.anchor === 'top' ? p.y : p.y - hUnits / 2;
      var pl = els.placement;
      pl.style.left = (left / surface.viewW) * 100 + '%';
      pl.style.top = (top / surface.viewH) * 100 + '%';
      pl.style.width = (wUnits / surface.viewW) * 100 + '%';
      pl.style.height = (hUnits / surface.viewH) * 100 + '%';
      var mc = els.mockCanvas;
      var k = Math.min(1, 700 / long);
      mc.width = Math.round(comp.width * k);
      mc.height = Math.round(comp.height * k);
      var ctx = mc.getContext('2d');
      ctx.clearRect(0, 0, mc.width, mc.height);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(comp, 0, 0, mc.width, mc.height);
    }

    function updateLowRes(result) {
      if (!state.logoPreview || state.isVector || !result) {
        els.lowRes.hidden = true;
        return;
      }
      var sizeIn = selectedSizeIn();
      var comp = result.canvas;
      var long = Math.max(comp.width, comp.height);
      var logoPrintWIn = ((sizeIn * comp.width) / long) * result.logoWidthFraction;
      // Source pixels across the trimmed logo = working px / working scale /
      // original scale (back to the pixels in the file they uploaded).
      var srcPx = state.logoPreview.width / state.workingScale / state.originalScale;
      var dpi = srcPx / logoPrintWIn;
      root.setAttribute('data-effective-dpi', String(Math.round(dpi)));
      if (dpi < LOW_DPI_WARN) {
        var needPx = Math.ceil((LOW_DPI_WARN * logoPrintWIn) / 50) * 50;
        els.lowResText.textContent =
          'Heads up: your logo is low resolution for ' +
          sizeIn +
          '" (about ' +
          Math.round(dpi) +
          ' dpi), so it may print a little blurry. For a sharp print, upload a bigger file (at least ' +
          needPx +
          ' px wide) or pick a smaller size.';
        els.lowRes.hidden = false;
      } else {
        els.lowRes.hidden = true;
      }
    }

    function updateTextHint() {
      var t = textValue();
      if (els.textCount) els.textCount.textContent = (els.textInput.value || '').length + '/' + TEXT_MAX;
      els.textOptions.hidden = !t;
      var small = selectedSizeIn() <= 2.5 && t.length > 18;
      els.textHint.hidden = !small;
    }

    var arTimer = null;
    function render() {
      var logo = state.logoPreview;
      var empty = !logo && !state.prefillUrl;
      els.stage.toggleAttribute('data-empty', empty);
      els.tools.hidden = empty;
      els.step2.hidden = empty;
      els.verify.hidden = empty;
      updateTextHint();

      // Background controls
      var s = bgSettings();
      els.bgAlready.hidden = !s.already;
      els.bgToggle.closest('[data-cx-bg-toggle-row]').hidden = s.already || !!state.prefillUrl;
      els.bgControls.hidden = !s.remove;
      els.bgHint.hidden = !(s.remove && state.border && state.border.uniformity < 0.55);
      if (els.cropChip) els.cropChip.hidden = !!state.prefillUrl || !state.original;

      if (!logo) {
        lastComposite = null;
        if (!state.prefillUrl) {
          els.placement.hidden = true;
          if (els.arImg) els.arImg.removeAttribute('src');
          els.patchDims.textContent = '';
        }
        updateLowRes(null);
        refreshSubmitGate();
        return;
      }

      var result = composeExact(logo, textOpts(), 900);
      lastComposite = result;
      els.placement.hidden = false;
      drawCheckerPreview(result.canvas);
      placeOnMockup(result.canvas);
      updateLowRes(result);

      var sizeIn = selectedSizeIn();
      var c = result.canvas;
      var long = Math.max(c.width, c.height);
      var w = (sizeIn * c.width) / long;
      var h = (sizeIn * c.height) / long;
      els.patchDims.textContent = 'Your patch: about ' + w.toFixed(1) + '" x ' + h.toFixed(1) + '"';

      clearTimeout(arTimer);
      arTimer = setTimeout(function () {
        if (els.arImg && lastComposite) els.arImg.src = lastComposite.canvas.toDataURL('image/png');
      }, 250);
      refreshSubmitGate();
    }

    var rafPending = false;
    function scheduleReprocess() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        reprocessPreview();
      });
    }

    /* ---------- upload ---------- */

    function resetLogo() {
      state.original = null;
      state.crop = null;
      state.working = null;
      state.logoPreview = null;
      state.border = null;
      state.prefillUrl = null;
      state.isVector = false;
      var stale = els.placement.querySelector('[data-cx-prefill-img]');
      if (stale) stale.remove();
      els.mockCanvas.hidden = false;
      els.bgToggle.checked = false;
      els.bgEverywhere.checked = false;
      els.bgStrength.value = els.bgStrength.getAttribute('data-default') || '35';
    }

    function acceptSource(canvas, isVector, fileScale) {
      var stale = els.placement.querySelector('[data-cx-prefill-img]');
      if (stale) stale.remove();
      els.mockCanvas.hidden = false;
      var capped = scaleCanvas(canvas, SOURCE_CAP_PX);
      state.original = capped.canvas;
      state.originalScale = capped.scale * (fileScale || 1);
      state.isVector = isVector;
      state.crop = null;
      state.prefillUrl = null;
      rebuildWorking();
      // Sensible default: a logo on a solid, uniform background almost
      // always wants that background gone (it would otherwise print as a
      // visible box). Pre-tick it; the customer can untick.
      var b = state.border;
      els.bgToggle.checked = !!(b && !b.alreadyTransparent && b.uniformity >= 0.6);
      els.bgEverywhere.checked = false;
      reprocessPreview();
    }

    els.file.addEventListener('change', function () {
      var file = els.file.files && els.file.files[0];
      if (!file) return;
      clearFileError();
      if (file.size > MAX_FILE_BYTES) {
        els.file.value = '';
        showFileError('That file is too large (max 20MB). Please choose a smaller file.');
        return;
      }
      var isSvg = /svg/i.test(file.type) || /\.svg$/i.test(file.name || '');
      state.busy = true;
      refreshSubmitGate();
      var job = isSvg ? rasteriseSvg(file) : rasteriseBitmap(file);
      job
        .then(function (res) {
          acceptSource(res.canvas, res.isVector, 1);
        })
        .catch(function (err) {
          els.file.value = '';
          resetLogo();
          if (err && err.code === 'dims') {
            showFileError("That image's dimensions are too large (max 8000px). Please choose a smaller file.");
          } else if (isSvg) {
            showFileError("We couldn't read that SVG in this browser. Please upload your logo as a PNG or JPG instead.");
          } else {
            showFileError('That file could not be read as an image. Please upload a PNG, JPG, WEBP or SVG.');
          }
          render();
        })
        .then(function () {
          state.busy = false;
          refreshSubmitGate();
        });
    });

    function openPicker() {
      els.file.click();
    }
    if (els.stageUpload) els.stageUpload.addEventListener('click', openPicker);
    if (els.replaceChip) els.replaceChip.addEventListener('click', openPicker);

    /* ---------- background controls ---------- */

    els.bgToggle.addEventListener('change', function () {
      state.prefillDirty = true;
      scheduleReprocess();
    });
    els.bgStrength.addEventListener('input', scheduleReprocess);
    els.bgEverywhere.addEventListener('change', scheduleReprocess);

    /* ---------- text controls ---------- */

    els.textInput.addEventListener('input', function () {
      if (els.textInput.value.length > TEXT_MAX) els.textInput.value = els.textInput.value.slice(0, TEXT_MAX);
      state.prefillDirty = true;
      render();
    });
    els.fontSelect.addEventListener('change', function () {
      var f = selectedFont();
      ensureFont(f).then(render);
      render();
    });
    els.colourRadios.forEach(function (r) {
      r.addEventListener('change', render);
    });
    if (els.colourCustom) {
      els.colourCustom.addEventListener('input', function () {
        els.colourCustomRadio.checked = true;
        var dot = els.colourCustomRadio.nextElementSibling.querySelector('[data-cx-colour-dot]');
        if (dot) dot.style.background = els.colourCustom.value;
        render();
      });
    }

    function ensureFont(font) {
      if (!document.fonts || !document.fonts.load) return Promise.resolve();
      return document.fonts.load(fontString(font, 40), 'AaBb').catch(function () {});
    }
    // Warm every font up front so the first text render is never in a
    // fallback face (canvas does not re-draw itself when a font arrives).
    Promise.all(
      Object.keys(FONTS).map(function (k) {
        return ensureFont(FONTS[k]);
      })
    ).then(render);

    /* ---------- mockup surface swatches ---------- */

    els.swatches.forEach(function (sw) {
      sw.addEventListener('change', function () {
        if (!sw.checked) return;
        els.stage.style.setProperty('--cx-surface', sw.getAttribute('data-surface-colour'));
        els.stage.style.setProperty('--cx-surface-edge', sw.getAttribute('data-surface-edge'));
        els.stage.setAttribute('data-surface-tone', sw.getAttribute('data-surface-tone'));
      });
    });

    /* ---------- crop (own modal, never the shared crop.js) ---------- */

    var crop = {
      stage: cropModal && cropModal.querySelector('[data-cx-crop-stage]'),
      img: cropModal && cropModal.querySelector('[data-cx-crop-image]'),
      box: cropModal && cropModal.querySelector('[data-cx-crop-box]'),
      confirm: cropModal && cropModal.querySelector('[data-cx-crop-confirm]'),
      reset: cropModal && cropModal.querySelector('[data-cx-crop-reset]'),
      rect: { x: 0, y: 0, width: 0, height: 0 },
    };
    var MIN_BOX = 30;

    // rect is kept relative to the image; the image can sit centred / padded
    // inside the stage, so add its offset when positioning the box.
    function applyCropBox() {
      crop.box.style.left = crop.img.offsetLeft + crop.rect.x + 'px';
      crop.box.style.top = crop.img.offsetTop + crop.rect.y + 'px';
      crop.box.style.width = crop.rect.width + 'px';
      crop.box.style.height = crop.rect.height + 'px';
    }

    function initCropBox() {
      var w = crop.img.clientWidth;
      var h = crop.img.clientHeight;
      var o = state.original;
      if (state.crop && o) {
        var sx = w / o.width;
        var sy = h / o.height;
        crop.rect = { x: state.crop.x * sx, y: state.crop.y * sy, width: state.crop.w * sx, height: state.crop.h * sy };
      } else {
        crop.rect = { x: 0, y: 0, width: w, height: h };
      }
      applyCropBox();
    }

    if (crop.stage && crop.img && crop.box && crop.confirm) {
      if (els.cropChip) {
        els.cropChip.addEventListener('click', function () {
          if (!state.original) return;
          // Show the full (uncropped) source so the customer can always
          // widen a previous crop again.
          var preview = scaleCanvas(state.original, 1400).canvas;
          crop.img.onload = function () {
            requestAnimationFrame(initCropBox);
          };
          crop.img.src = preview.toDataURL('image/png');
        });
      }

      var startDrag = function (evt, mode, handle) {
        evt.preventDefault();
        var sx = evt.clientX;
        var sy = evt.clientY;
        var start = { x: crop.rect.x, y: crop.rect.y, width: crop.rect.width, height: crop.rect.height };
        var W = crop.img.clientWidth;
        var H = crop.img.clientHeight;
        function onMove(m) {
          var dx = m.clientX - sx;
          var dy = m.clientY - sy;
          if (mode === 'move') {
            crop.rect.x = clamp(start.x + dx, 0, W - start.width);
            crop.rect.y = clamp(start.y + dy, 0, H - start.height);
          } else {
            var l = start.x;
            var t = start.y;
            var r = start.x + start.width;
            var b = start.y + start.height;
            if (handle === 'nw' || handle === 'sw') l = Math.min(start.x + dx, r - MIN_BOX);
            if (handle === 'ne' || handle === 'se') r = Math.max(r + dx, l + MIN_BOX);
            if (handle === 'nw' || handle === 'ne') t = Math.min(start.y + dy, b - MIN_BOX);
            if (handle === 'sw' || handle === 'se') b = Math.max(b + dy, t + MIN_BOX);
            l = Math.max(0, l);
            t = Math.max(0, t);
            r = Math.min(W, r);
            b = Math.min(H, b);
            crop.rect = { x: l, y: t, width: r - l, height: b - t };
          }
          applyCropBox();
        }
        function onUp() {
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
        }
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
      };

      crop.box.addEventListener('pointerdown', function (evt) {
        if (evt.target.hasAttribute('data-cx-crop-handle')) return;
        startDrag(evt, 'move', null);
      });
      crop.box.querySelectorAll('[data-cx-crop-handle]').forEach(function (hEl) {
        hEl.addEventListener('pointerdown', function (evt) {
          evt.stopPropagation();
          startDrag(evt, 'resize', hEl.getAttribute('data-cx-crop-handle'));
        });
      });

      if (crop.reset) {
        crop.reset.addEventListener('click', function () {
          crop.rect = { x: 0, y: 0, width: crop.img.clientWidth, height: crop.img.clientHeight };
          applyCropBox();
        });
      }

      crop.confirm.addEventListener('click', function () {
        var o = state.original;
        if (!o || !crop.img.clientWidth) return;
        var kx = o.width / crop.img.clientWidth;
        var ky = o.height / crop.img.clientHeight;
        var c = {
          x: Math.round(crop.rect.x * kx),
          y: Math.round(crop.rect.y * ky),
          w: Math.round(crop.rect.width * kx),
          h: Math.round(crop.rect.height * ky),
        };
        c.w = Math.max(4, Math.min(c.w, o.width - c.x));
        c.h = Math.max(4, Math.min(c.h, o.height - c.y));
        var full = c.x === 0 && c.y === 0 && c.w === o.width && c.h === o.height;
        state.crop = full ? null : c;
        rebuildWorking();
        reprocessPreview();
        if (typeof cropModal.hide === 'function') cropModal.hide();
      });

      window.addEventListener('resize', function () {
        if (crop.img.clientWidth) {
          crop.rect.width = Math.min(crop.rect.width, crop.img.clientWidth);
          crop.rect.height = Math.min(crop.rect.height, crop.img.clientHeight);
          crop.rect.x = clamp(crop.rect.x, 0, crop.img.clientWidth - crop.rect.width);
          crop.rect.y = clamp(crop.rect.y, 0, crop.img.clientHeight - crop.rect.height);
          applyCropBox();
        }
      });
    }

    /* ---------- final print file ---------- */

    // Full-resolution pass of exactly the same pipeline the preview uses.
    function buildPrintCanvas() {
      var o = state.original;
      var c = state.crop;
      var cropped = o;
      if (c) {
        cropped = makeCanvas(c.w, c.h);
        cropped.getContext('2d').drawImage(o, c.x, c.y, c.w, c.h, 0, 0, c.w, c.h);
      }
      var logo = processLogo(cropped);
      if (!logo) return null;
      var longEdge = Math.round(maxSizeIn * PRINT_DPI);
      return composeExact(logo, textOpts(), longEdge).canvas;
    }

    function exportPrintFile() {
      return new Promise(function (resolve, reject) {
        var canvas;
        try {
          canvas = buildPrintCanvas();
        } catch (e) {
          reject(new Error('We could not prepare your design in this browser. Please try a PNG or JPG file.'));
          return;
        }
        if (!canvas) {
          reject(new Error('Your logo came out empty. Please lower the background strength and try again.'));
          return;
        }
        canvas.toBlob(function (blob) {
          if (!blob) {
            reject(new Error('We could not prepare your design. Please try again.'));
            return;
          }
          resolve(new File([blob], 'business-logo-patch.png', { type: 'image/png' }));
        }, 'image/png');
      });
    }
    // Exposed for verification harnesses (and support debugging).
    root.cxLogoBuildPrintCanvas = buildPrintCanvas;

    /* ---------- size / custom size / price (fork of multi-add.js) ---------- */

    function clearCustomSizeError() {
      state.customSizeHasError = false;
      if (!els.customSizeError) return;
      els.customSizeError.hidden = true;
      els.customSizeErrorText.textContent = '';
      if (els.customSizeField) els.customSizeField.classList.remove('field--with-error');
    }
    function showCustomSizeError(msg) {
      state.customSizeHasError = true;
      if (!els.customSizeError) return;
      els.customSizeErrorText.textContent = msg;
      els.customSizeError.hidden = false;
      if (els.customSizeField) els.customSizeField.classList.add('field--with-error');
    }
    function clearCustomSizeConfirm() {
      if (!els.customSizeConfirm) return;
      els.customSizeConfirm.hidden = true;
      els.customSizeConfirm.textContent = '';
    }
    function clearCustomSizeDriven() {
      var m = root.querySelector('[data-primary-size-radio][data-custom-size-driven]');
      if (m) m.removeAttribute('data-custom-size-driven');
    }
    function validateCustomSize(raw) {
      clearCustomSizeError();
      if (!/^\d+(\.\d)?$/.test(raw)) {
        showCustomSizeError('Enter a size in inches, up to 1 decimal place (e.g. 3.5).');
        return null;
      }
      var v = Math.round(parseFloat(raw) * 10) / 10;
      var min = parseFloat(els.customSize.dataset.minSize);
      var max = parseFloat(els.customSize.dataset.maxSize);
      if (v < min || v > max) {
        showCustomSizeError('Size must be between ' + min + '" and ' + max + '".');
        return null;
      }
      return v;
    }

    if (els.customSize) {
      els.customSize.addEventListener('input', function () {
        var raw = els.customSize.value.trim();
        if (!raw) clearCustomSizeError();
        else validateCustomSize(raw);
        refreshSubmitGate();
      });
      els.customSize.addEventListener('blur', function () {
        var raw = els.customSize.value.trim();
        clearCustomSizeConfirm();
        state.customSizeValue = null;
        if (!raw) {
          clearCustomSizeError();
          render();
          return;
        }
        var v = validateCustomSize(raw);
        if (v === null) {
          render();
          return;
        }
        var best = null;
        var bestSize = null;
        root.querySelectorAll('[data-primary-size-radio]').forEach(function (r) {
          var s = sizeFromRadio(r);
          if (s === null || s + 0.001 < v) return;
          if (bestSize === null || s < bestSize) {
            bestSize = s;
            best = r;
          }
        });
        if (!best) {
          showCustomSizeError('Size must be between ' + els.customSize.dataset.minSize + '" and ' + els.customSize.dataset.maxSize + '".');
          render();
          return;
        }
        state.customSizeValue = String(v);
        clearCustomSizeDriven();
        root.dataset.syncingCustomSize = 'true';
        best.checked = true;
        best.setAttribute('data-custom-size-driven', '');
        best.dispatchEvent(new Event('change', { bubbles: true }));
        delete root.dataset.syncingCustomSize;
        if (els.customSizeConfirm) {
          els.customSizeConfirm.hidden = false;
          els.customSizeConfirm.textContent = "We'll size your design to exactly " + v + '".';
        }
        render();
      });
      els.customSize.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') els.customSize.blur();
      });
    }

    root.querySelectorAll('[data-primary-size-radio]').forEach(function (r) {
      r.addEventListener('change', function () {
        if (root.dataset.syncingCustomSize !== 'true') {
          if (els.customSize) els.customSize.value = '';
          state.customSizeValue = null;
          clearCustomSizeError();
          clearCustomSizeConfirm();
          clearCustomSizeDriven();
        }
        syncModalWithPrimary();
        render();
      });
    });

    function syncNativePrice() {
      var nativePrice = document.querySelector('[id^="price-"]');
      if (nativePrice) nativePrice.style.display = 'none';
      if (!els.price) return;
      var primary = root.querySelector('[data-primary-size-radio]:checked');
      if (!primary) {
        els.price.hidden = true;
        return;
      }
      var row = document.querySelector('[data-qty-input][data-variant-id="' + primary.value + '"]');
      var priceRow = row ? row.closest('.custom-personalization__size-row') : null;
      var txt = priceRow ? priceRow.querySelector('.custom-personalization__size-price').textContent : '';
      els.price.textContent = txt;
      els.price.hidden = !txt;
    }

    function refreshSubmitGate() {
      var complete = hasLogo();
      var sizeSelected = !!root.querySelector('[data-primary-size-radio]:checked');
      var label;
      var disabled = true;
      if (state.busy) label = 'Preparing your logo...';
      else if (state.customSizeHasError) label = 'Fix custom size';
      else if (!complete) label = 'Upload your logo';
      else if (!sizeSelected) label = 'Select size';
      else {
        label = 'Add to cart';
        disabled = false;
      }
      els.submit.disabled = disabled;
      els.submitText.textContent = label;
      if (els.openMoreSizes) els.openMoreSizes.disabled = disabled;
      syncNativePrice();
    }

    /* ---------- other sizes modal (document scoped: modal moves to body) ---------- */

    function syncRowEmphasis(input) {
      var row = input.closest('.custom-personalization__size-row');
      if (row) row.toggleAttribute('data-has-qty', (parseInt(input.value, 10) || 0) > 0);
    }

    document.querySelectorAll('[data-qty-decrease]').forEach(function (btn) {
      if (btn.dataset.cxBound) return;
      btn.dataset.cxBound = '1';
      btn.addEventListener('click', function () {
        var input = btn.closest('.custom-personalization__qty').querySelector('input');
        var min = parseInt(input.min, 10) || 0;
        input.value = Math.max(min, (parseInt(input.value, 10) || 0) - 1);
        syncRowEmphasis(input);
        if (input === els.primaryQty) syncModalWithPrimary();
      });
    });
    document.querySelectorAll('[data-qty-increase]').forEach(function (btn) {
      if (btn.dataset.cxBound) return;
      btn.dataset.cxBound = '1';
      btn.addEventListener('click', function () {
        var input = btn.closest('.custom-personalization__qty').querySelector('input');
        input.value = (parseInt(input.value, 10) || 0) + 1;
        syncRowEmphasis(input);
        if (input === els.primaryQty) syncModalWithPrimary();
      });
    });
    document.querySelectorAll('[data-qty-input]').forEach(function (input) {
      if (input.dataset.cxBound) return;
      input.dataset.cxBound = '1';
      input.addEventListener('input', function () {
        syncRowEmphasis(input);
      });
    });

    var modalSubmit = document.querySelector('[data-modal-submit]');
    if (modalSubmit && !modalSubmit.dataset.cxBound) {
      modalSubmit.dataset.cxBound = '1';
      modalSubmit.addEventListener('click', function () {
        els.submit.click();
      });
    }

    function syncModalWithPrimary() {
      var primary = root.querySelector('[data-primary-size-radio]:checked');
      var pid = primary ? primary.value : null;
      document.querySelectorAll('[data-qty-input]').forEach(function (input) {
        var row = input.closest('.custom-personalization__size-row');
        var soldOut = input.hasAttribute('data-sold-out');
        var isPrimary = pid && input.dataset.variantId === pid;
        var wasPrimary = row && row.hasAttribute('data-linked-to-primary');
        if (isPrimary) {
          input.value = els.primaryQty.value;
          input.disabled = true;
          if (row) row.setAttribute('data-linked-to-primary', '');
        } else {
          if (wasPrimary) {
            input.value = 0;
            if (row) row.removeAttribute('data-has-qty');
          }
          if (!soldOut) input.disabled = false;
          if (row) row.removeAttribute('data-linked-to-primary');
        }
      });
    }
    if (els.openMoreSizes) els.openMoreSizes.addEventListener('click', syncModalWithPrimary);
    els.primaryQty.addEventListener('input', syncModalWithPrimary);

    function collectSelections() {
      var by = {};
      var primary = root.querySelector('[data-primary-size-radio]:checked');
      var pid = primary ? primary.value : null;
      if (primary) {
        var pq = parseInt(els.primaryQty.value, 10) || 0;
        if (pq > 0) by[pid] = pq;
      }
      document.querySelectorAll('[data-qty-input]').forEach(function (input) {
        if (input.dataset.variantId === pid) return;
        var qn = parseInt(input.value, 10) || 0;
        if (qn > 0) by[input.dataset.variantId] = (by[input.dataset.variantId] || 0) + qn;
      });
      return Object.keys(by).map(function (id) {
        return { variantId: id, quantity: by[id] };
      });
    }

    /* ---------- cart ---------- */

    function extraProperties() {
      var props = {};
      var t = textValue();
      if (t) {
        // Quoted so the value can never start with "http" even when the
        // customer types their website (see header comment).
        props['Text Line'] = '"' + t + '"';
        props['Text Font'] = selectedFontLabel();
        props['Text Colour'] = selectedColourLabel();
      }
      var s = bgSettings();
      if (state.prefillUrl) props['Background'] = 'Kept from previous design';
      else if (s.already) props['Background'] = 'Already transparent';
      else props['Background'] = s.remove ? 'Removed on site' : 'Kept as uploaded';
      if (state.customSizeValue) props['Custom Size (inches)'] = state.customSizeValue;
      return props;
    }

    function showCartError(msg) {
      els.cartErrorText.textContent = msg;
      els.cartError.hidden = false;
    }
    function clearCartError() {
      els.cartError.hidden = true;
      els.cartErrorText.textContent = '';
    }

    function setLoading(loading) {
      els.submit.toggleAttribute('aria-disabled', loading);
      els.submit.classList.toggle('loading', loading);
      var sp = els.submit.querySelector('.loading__spinner');
      if (sp) sp.classList.toggle('hidden', !loading);
      if (modalSubmit) {
        modalSubmit.disabled = loading;
        modalSubmit.classList.toggle('loading', loading);
        var msp = modalSubmit.querySelector('.loading__spinner');
        if (msp) msp.classList.toggle('hidden', !loading);
      }
    }

    function postJson(body) {
      return fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json();
      });
    }

    function addFirstWithFile(variantId, quantity, file, props, sectionsParam) {
      var fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', quantity);
      fd.append('properties[Custom Image]', file);
      Object.keys(props).forEach(function (k) {
        fd.append('properties[' + k + ']', props[k]);
      });
      if (sectionsParam) {
        fd.append('sections', sectionsParam);
        fd.append('sections_url', window.location.pathname);
      }
      return fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      }).then(function (r) {
        return r.json();
      });
    }

    function merge(a, b) {
      var o = {};
      Object.keys(a).forEach(function (k) {
        o[k] = a[k];
      });
      Object.keys(b).forEach(function (k) {
        o[k] = b[k];
      });
      return o;
    }

    function renderCartSections(response) {
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.renderContents === 'function' && response.sections) cart.renderContents(response);
      var cd = document.querySelector('cart-drawer');
      if (cd) cd.classList.remove('is-empty');
      var cdi = document.querySelector('cart-drawer-items');
      if (cdi) cdi.classList.remove('is-empty');
    }

    function resetAfterAdd() {
      els.file.value = '';
      resetLogo();
      els.textInput.value = '';
      els.primaryQty.value = 1;
      document.querySelectorAll('[data-qty-input]').forEach(function (input) {
        input.value = 0;
        if (!input.hasAttribute('data-sold-out')) input.disabled = false;
        var row = input.closest('.custom-personalization__size-row');
        if (row) {
          row.removeAttribute('data-linked-to-primary');
          row.removeAttribute('data-has-qty');
        }
      });
      var opener = root.querySelector('modal-opener[data-modal]');
      var modal = opener ? document.querySelector(opener.getAttribute('data-modal')) : null;
      if (modal && typeof modal.hide === 'function') modal.hide();
      if (els.customSize) els.customSize.value = '';
      state.customSizeValue = null;
      clearCustomSizeError();
      clearCustomSizeConfirm();
      clearCustomSizeDriven();
      clearCartError();
      render();
    }

    els.submit.addEventListener('click', function () {
      clearCartError();
      var selections = collectSelections();
      if (!selections.length) {
        showCartError('Please choose a quantity for at least one size.');
        return;
      }
      if (!hasLogo()) {
        showCartError('Please upload your logo.');
        return;
      }
      if (state.customSizeHasError) {
        showCartError('Please fix the custom size, or clear it to use a standard size.');
        return;
      }
      setLoading(true);
      var props = extraProperties();
      var sectionsParam = 'cart-drawer,cart-icon-bubble';
      var first = selections[0];
      var rest = selections.slice(1);
      var firstSections = rest.length === 0 ? sectionsParam : null;

      // Unchanged edit-from-cart design: reuse the hosted file as-is.
      var reuseUrl = state.prefillUrl && !textValue() ? state.prefillUrl : null;
      var firstReq = reuseUrl
        ? (function () {
            var body = { id: first.variantId, quantity: first.quantity, properties: merge({ 'Custom Image': reuseUrl }, props) };
            if (firstSections) {
              body.sections = firstSections;
              body.sections_url = window.location.pathname;
            }
            return postJson(body);
          })()
        : exportPrintFile().then(function (file) {
            return addFirstWithFile(first.variantId, first.quantity, file, props, firstSections);
          });

      firstReq
        .then(function (firstRes) {
          if (firstRes.status) throw new Error(firstRes.description || firstRes.message || 'Could not add to cart.');
          if (!rest.length) return firstRes;
          var bulkProps = merge({ 'Custom Image': firstRes.properties['Custom Image'] }, props);
          var body = {
            items: rest.map(function (s) {
              return { id: s.variantId, quantity: s.quantity, properties: bulkProps };
            }),
            sections: sectionsParam,
            sections_url: window.location.pathname,
          };
          return postJson(body).then(function (res) {
            if (res.status || res.description) throw new Error(res.description || res.message || 'Could not add all sizes to cart.');
            return res;
          });
        })
        .then(function (finalRes) {
          renderCartSections(finalRes);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-logo-customiser', productVariantId: first.variantId, cartData: finalRes });
          }
          resetAfterAdd();
        })
        .catch(function (err) {
          showCartError((err && err.message) || 'Something went wrong adding this to your cart. Please try again.');
        })
        .then(function () {
          setLoading(false);
        });
    });

    /* ---------- edit from cart ---------- */

    var params = new URLSearchParams(window.location.search);
    if (params.get('edit_variant')) {
      var er = root.querySelector('[data-primary-size-radio][value="' + params.get('edit_variant') + '"]');
      if (er) er.checked = true;
      if (params.get('edit_qty')) els.primaryQty.value = params.get('edit_qty');
      var editImage = params.get('edit_image');
      if (editImage && /^https:\/\/([a-z0-9-]+\.)*(shopify\.com|shopifycdn\.net|slapthatpatch\.com)\//i.test(editImage)) {
        // Load the finished design back in as the "logo". With CORS it is
        // fully editable (add text, re-crop); without, it can still be
        // re-ordered as-is via its URL.
        loadImage(editImage, true)
          .then(function (img) {
            var c = makeCanvas(img.naturalWidth, img.naturalHeight);
            c.getContext('2d').drawImage(img, 0, 0);
            c.getContext('2d').getImageData(0, 0, 1, 1);
            acceptSource(c, false, 1);
            els.bgToggle.checked = false;
            reprocessPreview();
          })
          .catch(function () {
            state.prefillUrl = editImage;
            els.placement.hidden = false;
            var img2 = new Image();
            img2.onload = function () {
              placeOnMockup(makeCanvas(img2.naturalWidth, img2.naturalHeight));
              img2.className = 'cx-logo__prefill-img';
              img2.setAttribute('data-cx-prefill-img', '');
              img2.alt = 'Your previous design';
              els.mockCanvas.hidden = true;
              els.placement.appendChild(img2);
              if (els.arImg) els.arImg.src = editImage;
              render();
            };
            img2.src = editImage;
          });
      }
    }

    // Default size pre-selected server-side; make price + mockup reflect it.
    syncModalWithPrimary();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-cx-logo]').forEach(function (root) {
      if (root.dataset.cxLogoReady) return;
      root.dataset.cxLogoReady = '1';
      init(root);
    });
  }

  // Exposed for the static verification harness only.
  window.CxLogoInternals = {
    analyseBorder: analyseBorder,
    removeBackground: removeBackground,
    trimTransparent: trimTransparent,
    compose: compose,
    composeExact: composeExact,
    layoutText: layoutText,
    FONTS: FONTS,
    SURFACES: SURFACES,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
